import { useCallback, useEffect, useState } from "react";

import CloseButton from "components/layout/CloseButton";
import FunctionalButton from "components/layout/FunctionalButton";
import AnimatedModal from "components/layout/motion/AnimatedModal";
import { addUser } from "lib/arcAuthApi";

interface AddUserDialogProps {
  visible: boolean;
  onClose: () => void;
  onSaved: () => void;
}

const INPUT_CLASS =
  "w-full rounded-r5 border border-black80 bg-black70 px-3 py-2 text-h5 text-white placeholder:text-black60 focus:border-primary2 focus:outline-none";

const AddUserDialog: React.FC<AddUserDialogProps> = ({ visible, onClose, onSaved }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (visible) {
      setUsername("");
      setPassword("");
      setConfirmPassword("");
      setSaving(false);
      setError(null);
    }
  }, [visible]);

  const handleSubmit = useCallback(async () => {
    if (!username.trim()) {
      setError("Username is required");
      return;
    }
    if (!password) {
      setError("Password is required");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await addUser(username.trim(), password);
      onSaved();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to add user");
    } finally {
      setSaving(false);
    }
  }, [username, password, confirmPassword, onSaved, onClose]);

  return (
    <AnimatedModal
      visible={visible}
      onClose={onClose}
      className="w-full max-w-sm rounded-r10 border border-black80 bg-black90 p-4"
      zIndexClassName="z-[10050]"
    >
          <div className="mb-4 flex items-center justify-between">
            <p className="text-h4 font-semibold text-white">Add User</p>
            <CloseButton onClick={onClose} className="relative shrink-0" />
          </div>

          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1.5">
              <label className="text-h5 text-white">Username</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoFocus
                autoComplete="username"
                className={INPUT_CLASS}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-h5 text-white">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                className={INPUT_CLASS}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-h5 text-white">Confirm Password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
                className={INPUT_CLASS}
              />
            </div>
          </div>

          {error ? (
            <p className="mt-3 text-h5 text-red-400" role="alert">
              {error}
            </p>
          ) : null}

          <div className="mt-4 flex items-center justify-end gap-2">
            <FunctionalButton
              className="rounded-r5 border border-black80 bg-black70 px-4 py-2 hover:bg-black80 disabled:opacity-50"
              title="Cancel"
              titleClassName="!normal-case text-h5 font-semibold text-white"
              onClick={onClose}
              disabled={saving}
            />
            <FunctionalButton
              className="rounded-r5 border border-primary2 bg-primary2 px-4 py-2 hover:bg-primary1 disabled:opacity-50"
              title={saving ? "Adding…" : "Add"}
              titleClassName="!normal-case text-h5 font-semibold text-white"
              onClick={() => void handleSubmit()}
              disabled={saving}
            />
          </div>
    </AnimatedModal>
  );
};

export default AddUserDialog;
